import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import Telemetry, { ITelemetry2 } from '@core/Telemetry';
import { MomentChangeHandler } from '@core/interfaces/moment-change-handler';
import { NO_GROUP_ID } from '@api/Groups.api';
import { useStore } from '@ui/hooks/useStore';
import useDictionary from '@ui/hooks/useDictionary';
import getSortComparator from '@utils/getSortComparator';
import { IState } from './IState';

import { IRow, IColumn, IReport } from '@store/Reports.store';
import { toJS } from 'mobx';

const useReports = () => {
    const { devices, groups, map, app, user, lang, telemetry, reports } =
        useStore();
    const getWord = useDictionary();
    const fromDateFilterCurrent = moment(map.fromDateFilter);
    const toDateFilterCurrent = moment(map.toDateFilter);

    const initState: IState = {
        firstLoading: true,
        selectedDevice: '',
        selectedGroup: 'allGroups',
        fromDateFilterCurrent,
        toDateFilterCurrent,
        dateFilter: map.dateFilter,
    };
    const [state, setState] = useState(initState);

    const generateDefaultReport = (): IReport => {
        const rows: IRow[] = devices.deviceList
            .filter((data) => {
                const groupId = state.selectedGroup;
                if (groupId === 'allGroups') return true;
                else if (data.groupId === groupId) return true;
            })
            .map((data, i) => {

                const id = i;
                const name = data.name || "";
                const address = data.address || "";
                const [{ name: group }] = groups.groupsList.filter(
                    (group) => group.id === data.groupId,
                );
                const imei = data.imei;
                const lastConnection = data.date
                    ? moment(data.date)
                          .utcOffset(user.settings.timezone ?? 0)
                          .format('HH:mm DD.MM.YYYY')
                    : '';

                const isGps = !!data.gpsCoord && !!data.gpsCoord.lat;
                const isLbs = !!data.lbsCoord && !!data.lbsCoord.lat;
                let coordType = '';
                let _lat;
                let _lon;
                if(isGps){
                    _lat = data.gpsCoord.lat;
                    _lon = data.gpsCoord.lon;
                    coordType = "GPS";
                }else if(isLbs){
                    _lat = data.lbsCoord.lat;
                    _lon = data.lbsCoord.lon;
                    coordType = "LBS";
                }


                let lat: string = '';
                let lon: string = '';
                if (_lat) lat = _lat.toFixed(6);
                if (_lon) lon = _lon.toFixed(6);
                let vin: string = data.additionalInfo.trailerVin || data.additionalInfo.carVin || '';
                let company: string = data.additionalInfo.company || '';
                let contractNumber: string = data.additionalInfo.contractNumber || '';

                const batteryVoltage = data.batteryVoltage;
                return {
                    id,
                    name,
                    group,
                    imei,
                    address,
                    lastConnection,
                    coordType,
                    latitude: lat,
                    longitude: lon,
                    batteryVoltage:
                        typeof batteryVoltage === 'number'
                            ? batteryVoltage.toFixed(2)
                            : '',
                    contractNumber,
                    vin,
                    company
                };
            });
        const lengths = devices.deviceList
            .filter((data) => {
                const groupId = state.selectedGroup;
                if (groupId === 'allGroups') return true;
                else if (data.groupId === groupId) return true;
            })
            .map(({ name, address }) => ({ name: name?.length || 0, address: address?.length || 0 }));

        let maxAddressLength = 150;
        let maxNameLength = 150;
        for(let i = 0; i < lengths.length; i++){
            const nameLength = lengths[i].name * 8;
            const addrLength = lengths[i].address * 7;
            if(nameLength > maxNameLength) maxNameLength = nameLength;
            if(addrLength > maxAddressLength) maxAddressLength = addrLength;
        }

        const colData = [
            {
                field: 'name',
                width: maxNameLength,
            },
            { field: 'group', width: 150 },
            { field: 'imei', width: 150 },
            { field: 'lastConnection', width: 200 },
            { field: 'coordType', width: 130 },
            { field: 'latitude', width: 130 },
            { field: 'longitude', width: 130 },
            { field: 'address', width: maxAddressLength },
            { field: 'batteryVoltage', width: 170 },
            { field: 'contractNumber', width: 200 },
            { field: 'vin', width: 200 },
            { field: 'company', width: 200 }
        ];
        const columns: IColumn[] = colData.map(({ field, width }) => {
            const { units } = Telemetry;
            let headerName = getWord(field);
            if (field in units) {
                const unit = getWord(units[field as keyof typeof units]);
                headerName = `${headerName}, ${unit}`;
            }
            return {
                field,
                headerName,
                width,
                minWidth: width,
                flex: 1,
                editable: false,
                sortable: true,
                filterable: false,
            };
        });
        return {
            columns,
            rows,
        };
    };

    const generateTelemetryReport = (): IReport => {
        if (telemetry.selectedDevice !== state.selectedDevice) {
            telemetry.setSelectedDevice(state.selectedDevice);
        }
        map.setDateFilter(state.dateFilter);
        const width = [
            130, 160, 160, 160, 110, 140, 200, 110, 180, 190, 190, 180, 180,
            210, 160, 160, 160, 160, 160, 160, 170, 170,
        ];
        const columns: IColumn[] = Telemetry.headers.map((field, i) => {
            const { units } = Telemetry;
            let headerName = getWord(field);
            const sortComparator = getSortComparator(field);
            if (field in units) {
                const unit = getWord(units[field as keyof typeof units]);
                headerName = `${headerName}, ${unit}`;
            }

            if (field === "address") {
                width[i] =
                    telemetry.currentTelemetry.reduce((acc, data) => {                  
                        if (!!data.address && data.address.length > acc) {
                            return data.address.length;
                        } else {
                            return acc;
                        }
                    }, 0) * 7;
            }
            
            const result = {
                field,
                headerName,
                sortComparator,
                width: width[i],
                minWidth: width[i],
                editable: false,
                sortable: true,
                hideable: true,
                filterable: false,
            };
            return result;
        });
        //columns.push({ field: 'address', width: 200, headerName: getWord("address") } as IColumn);
        const rows: IRow[] = [...telemetry.currentTelemetry]
            .sort((a, b) => b.ts_s - a.ts_s)
            .map((data, i) => {
                const id = i;
                const telemetry: ITelemetry2 = new Telemetry(data, {
                    timezone: user.settings.timezone ?? 0,
                }).getObject();
                telemetry.number = id + 1;
                telemetry.coordType = telemetry.coordType
                    ? getWord(telemetry.coordType)
                    : '';
                telemetry.event = getWord(telemetry.event);
                telemetry.sim1pr = getWord(telemetry.sim1pr);
                telemetry.sim2pr = getWord(telemetry.sim2pr);
                telemetry.pm = getWord(telemetry.pm);
                // telemetry.alarm1 = getWord(telemetry.alarm1);
                // telemetry.alarm2 = getWord(telemetry.alarm2);
                // telemetry.alarm3 = getWord(telemetry.alarm3);
                // telemetry.alarm4 = getWord(telemetry.alarm4);
                // telemetry.alarm5 = getWord(telemetry.alarm5);
                // telemetry.alarm6 = getWord(telemetry.alarm6);
                
                return {
                    ...telemetry,
                    address: (!!data.address ? data.address : 'Адрес неизвестен' ),
                    id,
                };
            });
        return {
            columns,
            rows,
        };
    };

    const handleReportChange: SelectProps<string>['onChange'] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const report = e.target.value;
            reports.setReport(report);
        },
        [],
    );
    const handleReportClick = useCallback(() => {
        let rows: IRow[] = [];
        let columns: IColumn[] = [];
        let data = { rows, columns };
        switch (reports.report) {
            case 'currentState':
                data = generateDefaultReport();
                reports.setColumnByNames(
                    data.columns.map(({ field }) => field),
                );
                break;
            case 'telemetry':
                data = generateTelemetryReport();
                reports.setColumnByNames(
                    data.columns.map(({ field }) => field),
                );
                break;
        }
        rows = data.rows;
        columns = data.columns;



        reports.setRows(rows);
        reports.setColumns(columns);
    }, [
        devices.deviceList,
        state.selectedDevice,
        state.selectedGroup,
        state.dateFilter,
        state.fromDateFilterCurrent,
        state.toDateFilterCurrent,
    ]);
    const handleDeviceChange: SelectProps<string>['onChange'] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const selectedDevice = e.target.value;
            if (selectedDevice)
                setState((prev) => ({ ...prev, selectedDevice }));
        },
        [],
    );
    const handleFromDateChange: MomentChangeHandler = useCallback((date) => {
        setState((prev) => ({ ...prev, fromDateFilterCurrent: date }));
        if (date.isValid()) {
            const tz = user.settings.timezone;
            map.setFromDateFilter(date.utc(true).subtract(tz, 'm').toDate());
        }
    }, []);
    const handleToDateChange: MomentChangeHandler = useCallback((date) => {
        setState((prev) => ({ ...prev, toDateFilterCurrent: date }));
        if (date.isValid()) {
            const tz = user.settings.timezone;
            map.setToDateFilter(date.utc(true).subtract(tz, 'm').toDate());
        }
    }, []);
    const handleDateFilterChange: SelectProps<string>['onChange'] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const dateFilter = e.target.value;
            setState((prev) => ({ ...prev, dateFilter }));
        },
        [],
    );
    const handleGroupChange: SelectProps<string>['onChange'] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const selectedGroup = e.target.value;
            if (selectedGroup) setState((prev) => ({ ...prev, selectedGroup }));
        },
        [],
    );
    const handlers = {
        handleReportChange,
        handleReportClick,
        handleDeviceChange,
        handleFromDateChange,
        handleToDateChange,
        handleDateFilterChange,
        handleGroupChange,
    };

    useEffect(() => {
        return () => {
            reports.resetRows();
            reports.resetColumns();
        };
    }, []);
    useEffect(() => {
        const groupsLen = groups.groupsList.length;
        const devicesLen = devices.deviceList.length;
        if (
            app.page === 'reports' &&
            state.firstLoading &&
            groupsLen > 0 &&
            devicesLen > 0 &&
            !map.selectedGroupId
        ) {
            let id = localStorage.getItem('groupId');
            if (!id) {
                id = NO_GROUP_ID;
            }
            const listLen = devices.deviceList
                .filter((device) => device.groupId === id)
                .map(({ imei }) => imei).length;
            if (listLen > 0) {
                map.setSelectedGroupId(id);
                setState((prev) => ({ ...prev, firstLoading: false }));
            }
        }
    }, [groups.groupsList, devices.deviceList, app.page]);
    useEffect(() => {
        if (reports.columns.length > 0) {
            handleReportClick();
        }
    }, [lang.lang]);
    useEffect(() => {
        if (reports.columns.length > 0 && reports.report === 'currentState') {
            handleReportClick();
        }
    }, [devices.deviceList]);
    useEffect(() => {
        if (reports.columns.length > 0 && reports.report === 'telemetry') {
            handleReportClick();
        }
    }, [telemetry.telemetryList]);
    useEffect(() => {
        setState((prev) => ({ ...prev, fromDateFilterCurrent }));
    }, [fromDateFilterCurrent.milliseconds()]);
    useEffect(() => {
        setState((prev) => ({ ...prev, toDateFilterCurrent }));
    }, [toDateFilterCurrent.milliseconds()]);
    useEffect(() => {
        if (reports.report === 'currentState') {
            if (groups.loading) reports.setLoading();
            else reports.resetLoading();
        }
    }, [groups.loading]);
    useEffect(() => {
        if (reports.report === 'telemetry') {
            if (telemetry.loading) reports.setLoading();
            else reports.resetLoading();
        }
    }, [telemetry.loading]);

    return {
        lang,
        groups,
        map,
        user,
        state,
        reports,
        devices,
        handlers,
    };
};

export default useReports;
